import React, { type FC } from "react";
import clsx from "clsx";
import styles from "./FilterButton.module.scss";
import {
    GradeSelectorFilterConfig,
    GradeSelectorFilterId,
    isMultipleChoiceFilter,
} from "../../../../../utils/filterConstants";
import Icon from "../../../../../../shared-components/css-modules/lexus/atoms/Icon";
import type { GradeSelectorFiltersType } from "../../../../../redux/slices/GradeSelectorFiltersSlice";
import Popover from "../../../../../../shared-components/css-modules/lexus/vendor/popover/Popover";
import Button from "../../../../../../shared-components/css-modules/lexus/atoms/Button";
import { GradeSelectorLabel } from "../../../../../utils/labels";
import Text from "../../../../../../shared-components/css-modules/lexus/atoms/Text";
import Radio from "../../../../../../shared-components/css-modules/lexus/molecules/Radio";
import type { FilterDataType } from "../../../../../utils/filterUtils";
import Checkbox from "../../../../../../shared-components/css-modules/lexus/molecules/Checkbox";
import type { IconVariantTypes } from "../../../../../../shared-components/css-modules/lexus/atoms/Icon.types";
import useFilterButton from "../../../../../hooks/useFilterButton";
import PriceFilter from "./PriceFilter";

type FilterButtonType = {
    filterId: GradeSelectorFilterId;
    activeFilters: GradeSelectorFiltersType["filters"] | undefined;
    filterData: FilterDataType[];
    hasActiveFilter: boolean;
};

const FilterButton: FC<FilterButtonType> = (props) => {
    const { filterId, activeFilters, filterData, hasActiveFilter } = props;

    const filterButton = useFilterButton(filterId, activeFilters, filterData);
    const { addFilter, clearFilters, handleColourInput, handlePriceInput, onOpenChange, setSelectedFilters } =
        filterButton;
    const { buttonLabel, buttonText, resultButtonLabel } = filterButton;
    const { filterResultAmount, isButtonDisabled, isOpen, selectedFilters } = filterButton;

    const icon: IconVariantTypes = isOpen ? "chevron-up" : hasActiveFilter ? "close" : "chevron-down";
    const filterIcon = GradeSelectorFilterConfig.find((config) => config.id === filterId)?.icon as IconVariantTypes;

    return (
        <Popover onOpenChangeListen={onOpenChange} placement="bottom-start">
            <Popover.Trigger>
                <Button
                    className={clsx(
                        styles["filter-button-wrapper"],
                        hasActiveFilter && !isOpen && styles.active,
                        isOpen && styles.isOpen,
                    )}
                    variant="clean"
                >
                    {/* Only show filter value for multiple choice filters. */}
                    {!isOpen && hasActiveFilter && !isMultipleChoiceFilter(filterId) ? (
                        <div className={styles["filter-button-value"]}>
                            {filterIcon && (!hasActiveFilter || isOpen) && <Icon variant={filterIcon} sizing="md" />}
                            <Text variant="apheleia-body">{buttonText}</Text>
                        </div>
                    ) : (
                        <div className={styles["filter-button-value"]}>
                            {filterIcon && (!hasActiveFilter || isOpen) && <Icon variant={filterIcon} sizing="md" />}
                            <GradeSelectorLabel label={buttonLabel} />
                        </div>
                    )}
                    <Icon
                        onClick={(event) => {
                            if (hasActiveFilter && !isOpen) {
                                event.stopPropagation();
                                event.preventDefault();
                                clearFilters();
                            }
                        }}
                        variant={icon}
                        sizing="sm"
                        aria-hidden="true"
                    />
                </Button>
            </Popover.Trigger>
            <Popover.Content variant="empty" usePortal={false}>
                <div className={styles["filter-button-content-wrapper"]}>
                    <div className={styles["filter-button-content"]}>
                        {filterData.map((value, index) =>
                            // Colour filters (filters with assets) are multiple choice (checkboxes).
                            filterId === GradeSelectorFilterId.Colour ? (
                                <Checkbox
                                    key={value.code}
                                    label={value.name}
                                    disabled={value.uniqueGradeBodyTypeIds.length === 0}
                                    swatch={value.swatch}
                                    className={styles.swatch}
                                    onChange={() => handleColourInput(value.code)}
                                    checked={selectedFilters.includes(value.code)}
                                />
                            ) : filterId === GradeSelectorFilterId.Price ? (
                                <PriceFilter
                                    key={value.code}
                                    value={value}
                                    onChange={(payload) => {
                                        handlePriceInput(payload, index.toString());
                                    }}
                                />
                            ) : (
                                <Radio
                                    key={value.code}
                                    label={value.name}
                                    disabled={value.uniqueGradeBodyTypeIds.length === 0}
                                    onChange={() => setSelectedFilters([index.toString()])}
                                    checked={!!selectedFilters.length && Number(selectedFilters) === index}
                                    amount={value.uniqueGradeBodyTypeIds.length}
                                    showLabelAmount={filterId !== GradeSelectorFilterId.Sort}
                                />
                            ),
                        )}
                    </div>
                    <footer>
                        <Popover.Close onClick={addFilter}>
                            <Button disabled={isButtonDisabled} variant="btn-default" sizing="full-width">
                                <GradeSelectorLabel
                                    label={resultButtonLabel}
                                    params={{
                                        amount: filterResultAmount.toString(),
                                    }}
                                />
                            </Button>
                        </Popover.Close>
                    </footer>
                </div>
            </Popover.Content>
        </Popover>
    );
};

export default FilterButton;
